import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useShift } from '@/hooks/useShift';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowRightLeft, User, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { ShiftTemplate, formatTimeDisplay } from '@/types/shift-templates';

export default function ShiftSwapPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { patientId } = useParams<{ patientId: string }>();
    const { checkIn } = useShift();

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [caregiverLinks, setCaregiverLinks] = useState<any[]>([]);
    const [selectedSwap, setSelectedSwap] = useState<{
        templateId: string;
        originalTemplateId: string | null;
    } | null>(null);

    // Get current user's template to know what we are swapping FROM
    const [currentUserLink, setCurrentUserLink] = useState<any>(null);

    useEffect(() => {
        if (patientId) {
            fetchCaregiversAndTemplates();
        }
    }, [patientId]);

    const fetchCaregiversAndTemplates = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // 1. Get all caregiver links for this patient (including self)
            const { data: links, error } = await supabase
                .from('patient_user_links')
                .select(`
          id,
          user_id,
          shift_template_id,
          shift_templates:shift_templates(*)
        `)
                .eq('patient_id', patientId)
                .eq('role', 'caregiver')
                .eq('active', true);

            if (error) throw error;

            // Filter out those without templates
            const validLinks = links?.filter(l => l.shift_template_id && l.shift_templates) || [];

            if (validLinks.length === 0) {
                setCaregiverLinks([]);
                setIsLoading(false);
                return;
            }

            // 2. Fetch profiles for these users manually
            const userIds = validLinks.map(l => l.user_id);
            const { data: profiles, error: profilesError } = await supabase
                .from('profiles')
                .select('user_id, full_name, avatar_url')
                .in('user_id', userIds);

            if (profilesError) throw profilesError;

            // 3. Merge data
            const enrichedLinks = validLinks.map(link => {
                const profile = profiles?.find(p => p.user_id === link.user_id);
                return {
                    ...link,
                    profiles: profile || null
                };
            });

            const myLink = enrichedLinks.find(l => l.user_id === user.id);
            const otherLinks = enrichedLinks.filter(l => l.user_id !== user.id);

            setCurrentUserLink(myLink);
            setCaregiverLinks(otherLinks);
        } catch (err) {
            console.error("Error loading swap options:", err);
            toast.error(t('shiftSwap.errorLoading'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSwap = async (targetTemplateId: string) => {
        if (!patientId) return;

        // In this simplified flow, we might just redirect back to CheckIn page with query params
        // OR handle the check-in directly here.

        // Let's redirect back to CheckInPage with a state parameter
        // Simpler: Set global state? URL params?
        // Let's do the check-in here directly if they select "Confirm Swap & Check In"

        setIsSubmitting(true);
        try {
            // Find current user's template ID (if any)
            const originalTemplateId = currentUserLink?.shift_template_id || null;

            // Just check in directly with the swapped template
            const shift = await checkIn({
                patientId,
                photoUrl: null,
                latitude: null, // We might need to ask for location here too or skip strict location for swap?
                longitude: null, // Let's assume standard check-in location logic is needed. 
                // Maybe safer to redirect back to CheckIn page with "swappedTemplateId"
                shiftTemplateId: targetTemplateId,
                isSwap: true,
                originalTemplateId: originalTemplateId
            });

            if (shift) {
                toast.success(t('shift.checkInSuccess'));
                navigate(`/app/caregiver/${patientId}`);
            }
        } catch (error: any) {
            console.error("Swap error:", error);
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Alternative: Redirect back to CheckIn with selected swap
    // This is better to reuse the Location logic in CheckInPage
    const selectSwapAndReturn = (template: ShiftTemplate) => {
        // We can't easily pass complex state via navigate(-1) unless we use state
        // For now, let's implement the simpler "Perform Swap Check-in HERE" approach
        // But we need location.
        // Re-implementing location here is redundant.
        // Let's assume we navigate to check-in with query param ?swapTemplateId=xyz

        navigate(`/app/patients/${patientId}/check-in?swapTemplateId=${template.id}&swapName=${encodeURIComponent(template.name)}`);
    };

    return (
        <AppLayout title={t('shiftSwap.title')} showBack onBack={() => navigate(-1)}>
            <div className="p-4 space-y-4">
                <div className="bg-warning/10 border border-warning/20 p-4 rounded-lg flex items-start gap-3">
                    <ArrowRightLeft className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <h3 className="font-semibold text-warning mb-1">{t('shiftSwap.whatIsThis')}</h3>
                        <p className="text-foreground/80 leading-relaxed">{t('shiftSwap.description')}</p>
                    </div>
                </div>

                <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="py-3">
                        <p className="text-sm text-muted-foreground text-center">
                            {t('shiftSwap.availableColleagues')}
                        </p>
                    </CardContent>
                </Card>

                {isLoading ? (
                    <div className="flex justify-center p-8">
                        <CareOSLoader size="md" />
                    </div>
                ) : caregiverLinks.length === 0 ? (
                    <div className="text-center p-8 border-2 border-dashed rounded-lg">
                        <p className="text-muted-foreground">{t('shiftSwap.noColleaguesAvailable')}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {caregiverLinks.map((link) => {
                            const template = link.shift_templates;
                            const profile = link.profiles;

                            return (
                                <Card
                                    key={link.id}
                                    className="overflow-hidden hover:border-primary/50 transition-colors cursor-pointer group"
                                    onClick={() => selectSwapAndReturn(template)}
                                >
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <SignedAvatar
                                            src={profile?.avatar_url || null}
                                            bucket="avatars"
                                            className="h-10 w-10"
                                            fallback={
                                                <User className="w-5 h-5 text-primary" />
                                            }
                                        />

                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{profile?.full_name || 'Caregiver'}</p>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Badge variant="outline" className="text-xs font-normal">
                                                    {template.name}
                                                </Badge>
                                            </div>
                                        </div>

                                        <div className="text-right">
                                            <Badge variant="secondary" className="flex items-center gap-1 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                                <Clock className="w-3 h-3" />
                                                {formatTimeDisplay(template.default_start_time)} - {formatTimeDisplay(template.default_end_time)}
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </AppLayout>
    );
}
